import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Separator } from "../../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { 
  Shield, Search, FileText, Users, Euro, Building2, Scale, 
  Calendar, Eye, Download, Loader2, Brain, Clock, TrendingUp, 
  AlertTriangle, CheckCircle2, Sparkles, ArrowRightLeft, ScrollText, 
  FileCheck, Database, History, Lightbulb, RefreshCw, Trash2,
  Archive, Copy, MoreVertical, FileOutput, AlertCircle, XCircle,
  Filter, SortAsc, SortDesc, FolderOpen, Star, StarOff
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Document categories config
const CATEGORIES = {
  statuts: { 
    icon: ScrollText, 
    color: "bg-indigo-100 text-indigo-600 border-indigo-200",
    label: "Statuts",
    description: "Constitution et modifications statutaires"
  },
  kbis: { 
    icon: FileCheck, 
    color: "bg-blue-100 text-blue-600 border-blue-200",
    label: "Kbis",
    description: "Extraits du registre du commerce"
  },
  bilan: { 
    icon: Euro, 
    color: "bg-green-100 text-green-600 border-green-200",
    label: "Bilans",
    description: "Liasses fiscales et comptes annuels"
  },
  pv_ago: { 
    icon: Users, 
    color: "bg-purple-100 text-purple-600 border-purple-200",
    label: "PV AGO",
    description: "Assemblees Generales Ordinaires"
  },
  pv_age: { 
    icon: Scale, 
    color: "bg-orange-100 text-orange-600 border-orange-200",
    label: "PV AGE",
    description: "Assemblees Generales Extraordinaires"
  },
  cession: { 
    icon: ArrowRightLeft, 
    color: "bg-amber-100 text-amber-600 border-amber-200",
    label: "Cessions",
    description: "Actes de cession de parts/actions"
  },
  generated: {
    icon: FileOutput,
    color: "bg-cyan-100 text-cyan-600 border-cyan-200",
    label: "Documents generes",
    description: "PV generes par l'application"
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'Europe/Paris'
    });
  } catch {
    return dateStr;
  }
};

const formatFileSize = (bytes) => {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function CoffreFortIntelligent({ onNavigate }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("documents");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortBy, setSortBy] = useState("date_desc"); // date_desc, date_asc, name, type
  const [showArchived, setShowArchived] = useState(false);
  
  // Data
  const [importedDocs, setImportedDocs] = useState([]);
  const [generatedDocs, setGeneratedDocs] = useState([]);
  const [knowledgeBase, setKnowledgeBase] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  
  // Preview
  const [showPreview, setShowPreview] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [previewTab, setPreviewTab] = useState("info");
  const [pdfContent, setPdfContent] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  
  // Dialogs
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Reclassification dialog
  const [showReclassifyDialog, setShowReclassifyDialog] = useState(false);
  const [docToReclassify, setDocToReclassify] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [reclassifyLoading, setReclassifyLoading] = useState(false);

  // Handle reclassification
  const handleReclassify = async () => {
    if (!docToReclassify || !newCategory) return;
    
    setReclassifyLoading(true);
    try {
      await axios.patch(`${API}/api/corporate/ocr/documents/${docToReclassify.id}/category`, {
        category: newCategory
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Document reclassé en "${CATEGORIES[newCategory]?.label || newCategory}"`);
      setShowReclassifyDialog(false);
      setDocToReclassify(null);
      setNewCategory("");
      fetchImportedDocs();
    } catch (error) {
      console.error("Error reclassifying document:", error);
      toast.error("Erreur lors de la reclassification");
    } finally {
      setReclassifyLoading(false);
    }
  };

  // Fetch imported documents from OCR history
  const fetchImportedDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/corporate/ocr/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImportedDocs(res.data.documents || []);
    } catch (error) {
      console.error("Error fetching imported documents:", error);
    }
  }, [token]);

  // Fetch generated documents (AGO/AGE PDFs)
  const fetchGeneratedDocs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/corporate/ocr/coffre/generated`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedDocs(res.data.documents || []);
    } catch (error) {
      console.error("Error fetching generated documents:", error);
    }
  }, [token]);

  // Fetch knowledge base
  const fetchKnowledgeBase = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/corporate/ocr/knowledge-base`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKnowledgeBase(res.data);
    } catch (error) {
      console.error("Error fetching knowledge base:", error);
    }
  }, [token]);

  // Fetch timeline
  const fetchTimeline = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/corporate/ocr/timeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimeline(res.data.events || []);
    } catch (error) {
      console.error("Error fetching timeline:", error);
    }
  }, [token]);

  // Detect duplicates
  const detectDuplicates = useCallback(() => {
    const allDocs = [...importedDocs, ...generatedDocs];
    const seen = new Map();
    const dups = [];
    
    allDocs.forEach(doc => {
      // Create fingerprint based on filename and size
      const fingerprint = `${doc.filename?.toLowerCase()}_${doc.file_size || 0}`;
      if (seen.has(fingerprint)) {
        dups.push({
          original: seen.get(fingerprint),
          duplicate: doc
        });
      } else {
        seen.set(fingerprint, doc);
      }
    });
    
    setDuplicates(dups);
  }, [importedDocs, generatedDocs]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchImportedDocs(),
        fetchGeneratedDocs(),
        fetchKnowledgeBase(),
        fetchTimeline()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchImportedDocs, fetchGeneratedDocs, fetchKnowledgeBase, fetchTimeline]);

  // Detect duplicates when docs change
  useEffect(() => {
    detectDuplicates();
  }, [detectDuplicates]);

  // Combine and process all documents
  const allDocuments = [
    ...importedDocs.map(d => ({ ...d, source: 'imported' })),
    ...generatedDocs.map(d => ({ ...d, source: 'generated', document_type: 'generated' }))
  ];

  // Filter documents
  const filteredDocs = allDocuments.filter(doc => {
    // Search filter
    const matchesSearch = !searchQuery || 
      doc.filename?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.document_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.title?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Category filter
    const matchesCategory = selectedCategory === "all" || 
      doc.document_type === selectedCategory ||
      (selectedCategory === "generated" && doc.source === "generated");
    
    // Archived filter
    const matchesArchived = showArchived ? doc.archived : !doc.archived;
    
    return matchesSearch && matchesCategory && matchesArchived;
  });

  // Sort documents
  const sortedDocs = [...filteredDocs].sort((a, b) => {
    switch (sortBy) {
      case "date_asc":
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      case "date_desc":
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      case "name":
        return (a.filename || a.title || "").localeCompare(b.filename || b.title || "");
      case "type":
        return (a.document_type || "").localeCompare(b.document_type || "");
      default:
        return 0;
    }
  });

  // Group documents by category
  const docsByCategory = sortedDocs.reduce((acc, doc) => {
    const cat = doc.source === "generated" ? "generated" : (doc.document_type || "autres");
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(doc);
    return acc;
  }, {});

  // Download document
  const downloadDocument = async (doc) => {
    try {
      let url, filename;
      
      if (doc.source === "generated") {
        // Generated document
        const res = await axios.get(`${API}/api/corporate/ocr/coffre/generated/${doc.id}/download`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        url = window.URL.createObjectURL(new Blob([res.data]));
        filename = doc.filename || `${doc.title}.pdf`;
      } else {
        // Imported document
        const res = await axios.get(`${API}/api/corporate/ocr/download/${doc.id}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        url = window.URL.createObjectURL(new Blob([res.data]));
        filename = doc.filename;
      }
      
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Erreur lors du telechargement");
    }
  };

  // Load PDF for preview
  const loadPdfPreview = async (doc) => {
    if (!doc || !doc.id) return;
    
    setPdfLoading(true);
    setPdfContent(null);
    
    try {
      let res;
      if (doc.source === "generated") {
        res = await axios.get(`${API}/api/corporate/ocr/coffre/generated/${doc.id}/pdf`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        res = await axios.get(`${API}/api/corporate/ocr/pdf/${doc.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      if (res.data.content_b64) {
        // Convert base64 to blob for better browser compatibility
        try {
          const byteCharacters = atob(res.data.content_b64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(blob);
          setPdfContent(blobUrl);
        } catch (e) {
          // Fallback to data URL if blob fails
          setPdfContent(`data:application/pdf;base64,${res.data.content_b64}`);
        }
      }
    } catch (error) {
      console.error("Error loading PDF:", error);
    } finally {
      setPdfLoading(false);
    }
  };

  // Handle document selection
  const handleSelectDoc = async (doc) => {
    setSelectedDoc(doc);
    setPreviewTab("info");
    setPdfContent(null);
    setShowPreview(true);
    
    // Fetch full details if imported doc
    if (doc.source === "imported" && (!doc.extracted_data || !doc.profile_data)) {
      try {
        const res = await axios.get(`${API}/api/corporate/ocr/preview/${doc.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Preserve source and merge with fetched data
        setSelectedDoc({ ...doc, ...res.data, source: 'imported' });
      } catch (error) {
        console.error("Error fetching document details:", error);
      }
    }
  };

  // Delete document
  const handleDelete = async () => {
    if (!docToDelete) return;
    
    setDeleteLoading(true);
    try {
      if (docToDelete.source === "generated") {
        await axios.delete(`${API}/api/corporate/ocr/coffre/generated/${docToDelete.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.delete(`${API}/api/corporate/ocr/${docToDelete.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success("Document supprime");
      setShowDeleteDialog(false);
      setDocToDelete(null);
      
      // Refresh
      if (docToDelete.source === "generated") {
        fetchGeneratedDocs();
      } else {
        fetchImportedDocs();
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Archive/Unarchive document
  const toggleArchive = async (doc) => {
    try {
      const endpoint = doc.source === "generated" 
        ? `${API}/api/corporate/ocr/coffre/generated/${doc.id}/archive`
        : `${API}/api/corporate/ocr/document/${doc.id}/archive`;
      
      await axios.post(endpoint, { archived: !doc.archived }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(doc.archived ? "Document desarchive" : "Document archive");
      
      // Refresh
      if (doc.source === "generated") {
        fetchGeneratedDocs();
      } else {
        fetchImportedDocs();
      }
    } catch (error) {
      toast.error("Erreur");
    }
  };

  // Mark as favorite
  const toggleFavorite = async (doc) => {
    try {
      const endpoint = doc.source === "generated"
        ? `${API}/api/corporate/ocr/coffre/generated/${doc.id}/favorite`
        : `${API}/api/corporate/ocr/document/${doc.id}/favorite`;
      
      await axios.post(endpoint, { favorite: !doc.favorite }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(doc.favorite ? "Retire des favoris" : "Ajoute aux favoris");
      
      if (doc.source === "generated") {
        fetchGeneratedDocs();
      } else {
        fetchImportedDocs();
      }
    } catch (error) {
      toast.error("Erreur");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const totalDocs = importedDocs.length + generatedDocs.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Coffre-Fort Intelligent</h2>
            <p className="text-sm text-gray-500">
              {totalDocs} document(s) • {knowledgeBase?.total_facts || 0} infos stockees
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          {duplicates.length > 0 && (
            <Badge variant="destructive" className="gap-1">
              <Copy className="w-3 h-3" />
              {duplicates.length} doublon(s) detecte(s)
            </Badge>
          )}
          <Button
            onClick={() => onNavigate && onNavigate("import")}
            className="gap-2 bg-purple-600 hover:bg-purple-700"
          >
            <Sparkles className="w-4 h-4" />
            Importer
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-indigo-500" />
              <div>
                <p className="text-2xl font-bold">{importedDocs.length}</p>
                <p className="text-xs text-gray-500">Importes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-cyan-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileOutput className="w-8 h-8 text-cyan-500" />
              <div>
                <p className="text-2xl font-bold">{generatedDocs.length}</p>
                <p className="text-xs text-gray-500">Generes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Brain className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{knowledgeBase?.total_facts || 0}</p>
                <p className="text-xs text-gray-500">Infos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{timeline.length}</p>
                <p className="text-xs text-gray-500">Evenements</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={`border-l-4 ${duplicates.length > 0 ? 'border-l-red-500' : 'border-l-gray-300'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Copy className={`w-8 h-8 ${duplicates.length > 0 ? 'text-red-500' : 'text-gray-400'}`} />
              <div>
                <p className="text-2xl font-bold">{duplicates.length}</p>
                <p className="text-xs text-gray-500">Doublons</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Duplicate Warning */}
      {duplicates.length > 0 && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-red-800">Doublons detectes</h4>
              <p className="text-sm text-red-600 mt-1">
                {duplicates.length} fichier(s) semble(nt) etre en double. Verifiez et supprimez les doublons si necessaire.
              </p>
              <div className="mt-2 space-y-1">
                {duplicates.slice(0, 3).map((dup, idx) => (
                  <p key={idx} className="text-xs text-red-500">
                    • {dup.duplicate.filename || dup.duplicate.title}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="documents" className="gap-2">
            <FolderOpen className="w-4 h-4" />
            <span className="hidden sm:inline">Documents</span>
          </TabsTrigger>
          <TabsTrigger value="knowledge" className="gap-2">
            <Brain className="w-4 h-4" />
            <span className="hidden sm:inline">Connaissances</span>
          </TabsTrigger>
          <TabsTrigger value="timeline" className="gap-2">
            <History className="w-4 h-4" />
            <span className="hidden sm:inline">Chronologie</span>
          </TabsTrigger>
        </TabsList>

        {/* Documents Tab */}
        <TabsContent value="documents" className="mt-6">
          {/* Search, Filter & Sort Bar */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Recherche intelligente (nom, type, contenu)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex gap-2">
              {/* Sort dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1">
                    {sortBy.includes('desc') ? <SortDesc className="w-4 h-4" /> : <SortAsc className="w-4 h-4" />}
                    Trier
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => setSortBy("date_desc")}>
                    Plus recent
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy("date_asc")}>
                    Plus ancien
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy("name")}>
                    Nom A-Z
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setSortBy("type")}>
                    Type
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              {/* Archive toggle */}
              <Button
                variant={showArchived ? "default" : "outline"}
                size="sm"
                onClick={() => setShowArchived(!showArchived)}
                className="gap-1"
              >
                <Archive className="w-4 h-4" />
                {showArchived ? "Archives" : "Actifs"}
              </Button>
            </div>
          </div>
          
          {/* Category Filters */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
            <Button
              variant={selectedCategory === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory("all")}
            >
              Tous ({totalDocs})
            </Button>
            {Object.entries(CATEGORIES).map(([key, config]) => {
              const count = key === "generated" 
                ? generatedDocs.length 
                : importedDocs.filter(d => d.document_type === key).length;
              if (count === 0 && selectedCategory !== key) return null;
              const Icon = config.icon;
              return (
                <Button
                  key={key}
                  variant={selectedCategory === key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(key)}
                  className="gap-1 whitespace-nowrap"
                >
                  <Icon className="w-3 h-3" />
                  {config.label} ({count})
                </Button>
              );
            })}
          </div>

          {/* Documents Grid */}
          {Object.entries(docsByCategory).length > 0 ? (
            <div className="space-y-6">
              {Object.entries(docsByCategory).map(([category, docs]) => {
                const config = CATEGORIES[category] || CATEGORIES.statuts;
                const Icon = config.icon;
                
                return (
                  <div key={category}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`p-1.5 rounded-lg ${config.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <h3 className="font-semibold text-gray-900">{config.label}</h3>
                      <Badge variant="secondary" className="text-xs">{docs.length}</Badge>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {docs.map(doc => (
                        <DocumentCard
                          key={doc.id}
                          doc={doc}
                          config={config}
                          duplicates={duplicates}
                          onSelect={() => handleSelectDoc(doc)}
                          onDownload={() => downloadDocument(doc)}
                          onDelete={() => { setDocToDelete(doc); setShowDeleteDialog(true); }}
                          onArchive={() => toggleArchive(doc)}
                          onFavorite={() => toggleFavorite(doc)}
                          onReclassify={() => { setDocToReclassify(doc); setNewCategory(doc.document_type || ""); setShowReclassifyDialog(true); }}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-lg border">
              <Shield className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {showArchived ? "Aucun document archive" : "Aucun document"}
              </h3>
              <p className="text-gray-500 mb-4">
                {showArchived 
                  ? "Vos documents archives apparaitront ici"
                  : "Importez ou generez vos premiers documents juridiques"
                }
              </p>
              {!showArchived && (
                <Button
                  onClick={() => onNavigate && onNavigate("import")}
                  className="gap-2 bg-purple-600 hover:bg-purple-700"
                >
                  <Sparkles className="w-4 h-4" />
                  Importer des documents
                </Button>
              )}
            </div>
          )}
        </TabsContent>

        {/* Knowledge Base Tab */}
        <TabsContent value="knowledge" className="mt-6">
          <KnowledgeBaseTab knowledgeBase={knowledgeBase} />
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="mt-6">
          <TimelineTab timeline={timeline} />
        </TabsContent>
      </Tabs>

      {/* Document Preview Sheet */}
      <Sheet open={showPreview} onOpenChange={(open) => {
        setShowPreview(open);
        if (!open) {
          setPdfContent(null);
          setPreviewTab("info");
        }
      }}>
        <SheetContent className="w-full sm:max-w-3xl p-0 flex flex-col">
          <SheetHeader className="px-6 py-4 border-b">
            <div className="flex items-center justify-between">
              <SheetTitle className="truncate pr-4">
                {selectedDoc?.filename || selectedDoc?.title}
              </SheetTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => selectedDoc && downloadDocument(selectedDoc)}
              >
                <Download className="w-4 h-4 mr-1" />
                Telecharger
              </Button>
            </div>
            
            {/* Preview Tabs */}
            <div className="flex gap-1 mt-3">
              <Button
                variant={previewTab === "info" ? "default" : "ghost"}
                size="sm"
                onClick={() => setPreviewTab("info")}
                className="gap-1"
              >
                <FileText className="w-3 h-3" />
                Infos
              </Button>
              <Button
                variant={previewTab === "pdf" ? "default" : "ghost"}
                size="sm"
                onClick={() => {
                  setPreviewTab("pdf");
                  if (!pdfContent && selectedDoc) {
                    loadPdfPreview(selectedDoc);
                  }
                }}
                className="gap-1"
              >
                <Eye className="w-3 h-3" />
                Visualiser
              </Button>
              {selectedDoc?.source === "imported" && (
                <Button
                  variant={previewTab === "data" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setPreviewTab("data")}
                  className="gap-1"
                >
                  <Database className="w-3 h-3" />
                  Donnees OCR
                </Button>
              )}
            </div>
          </SheetHeader>
          
          <div className="flex-1 overflow-hidden">
            {selectedDoc && (
              <>
                {previewTab === "info" && <InfoTab doc={selectedDoc} onNavigate={onNavigate} onClose={() => setShowPreview(false)} />}
                {previewTab === "pdf" && <PdfTab pdfContent={pdfContent} pdfLoading={pdfLoading} onReload={() => loadPdfPreview(selectedDoc)} />}
                {previewTab === "data" && <DataTab doc={selectedDoc} />}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Confirmer la suppression
            </DialogTitle>
            <DialogDescription>
              Etes-vous sur de vouloir supprimer "{docToDelete?.filename || docToDelete?.title}" ?
              Cette action est irreversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reclassification Dialog */}
      <Dialog open={showReclassifyDialog} onOpenChange={setShowReclassifyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-indigo-500" />
              Reclassifier le document
            </DialogTitle>
            <DialogDescription>
              Changer la catégorie de "{docToReclassify?.filename || docToReclassify?.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-500 mb-2">Catégorie actuelle: <span className="font-medium">{CATEGORIES[docToReclassify?.document_type]?.label || docToReclassify?.document_type || "Non classé"}</span></p>
            <p className="text-sm font-medium mb-2">Nouvelle catégorie :</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(CATEGORIES).map(([key, cat]) => {
                const Icon = cat.icon;
                return (
                  <Button
                    key={key}
                    variant={newCategory === key ? "default" : "outline"}
                    className={`justify-start gap-2 h-auto py-2 ${newCategory === key ? '' : cat.color}`}
                    onClick={() => setNewCategory(key)}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm">{cat.label}</span>
                  </Button>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReclassifyDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleReclassify}
              disabled={reclassifyLoading || !newCategory || newCategory === docToReclassify?.document_type}
            >
              {reclassifyLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Reclassifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Document Card Component
function DocumentCard({ doc, config, duplicates, onSelect, onDownload, onDelete, onArchive, onFavorite, onReclassify }) {
  const Icon = config.icon;
  const isDuplicate = duplicates.some(d => d.duplicate.id === doc.id);
  
  return (
    <Card 
      className={`group hover:shadow-md transition-all cursor-pointer border-l-4 ${config.color.split(' ')[0]} ${isDuplicate ? 'ring-2 ring-red-300' : ''}`}
      onClick={onSelect}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg ${config.color}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              {doc.favorite && <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />}
              <p className="font-medium text-gray-900 truncate text-sm">
                {doc.filename || doc.title}
              </p>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {formatDate(doc.created_at || doc.generated_at)}
              {doc.file_size && ` • ${formatFileSize(doc.file_size)}`}
            </p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {doc.source === "generated" ? (
                <Badge className="bg-cyan-100 text-cyan-700 text-xs">
                  <FileOutput className="w-3 h-3 mr-1" />
                  Genere
                </Badge>
              ) : doc.applied ? (
                <Badge className="bg-green-100 text-green-700 text-xs">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Applique
                </Badge>
              ) : doc.status === "completed" ? (
                <Badge className="bg-amber-100 text-amber-700 text-xs">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  A valider
                </Badge>
              ) : null}
              
              {isDuplicate && (
                <Badge variant="destructive" className="text-xs">
                  <Copy className="w-3 h-3 mr-1" />
                  Doublon
                </Badge>
              )}
              
              {doc.archived && (
                <Badge variant="secondary" className="text-xs">
                  <Archive className="w-3 h-3 mr-1" />
                  Archive
                </Badge>
              )}
            </div>
          </div>
          
          {/* Actions Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onClick={onSelect}>
                <Eye className="w-4 h-4 mr-2" />
                Voir
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDownload}>
                <Download className="w-4 h-4 mr-2" />
                Telecharger
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onFavorite}>
                {doc.favorite ? (
                  <>
                    <StarOff className="w-4 h-4 mr-2" />
                    Retirer des favoris
                  </>
                ) : (
                  <>
                    <Star className="w-4 h-4 mr-2" />
                    Ajouter aux favoris
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onArchive}>
                <Archive className="w-4 h-4 mr-2" />
                {doc.archived ? "Desarchiver" : "Archiver"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onReclassify} className="text-indigo-600">
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Reclassifier
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDelete} className="text-red-600">
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardContent>
    </Card>
  );
}

// Info Tab Component
function InfoTab({ doc, onNavigate, onClose }) {
  return (
    <div className="p-6 space-y-4 overflow-auto h-full">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-gray-500">Type</p>
          <p className="font-medium">{CATEGORIES[doc.document_type]?.label || doc.document_type || "Genere"}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Date</p>
          <p className="font-medium">{formatDate(doc.created_at || doc.generated_at)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Statut</p>
          {doc.source === "generated" ? (
            <Badge className="bg-cyan-100 text-cyan-700">Document genere</Badge>
          ) : doc.applied ? (
            <Badge className="bg-green-100 text-green-700">Applique au profil</Badge>
          ) : (
            <Badge variant="secondary">{doc.status}</Badge>
          )}
        </div>
        <div>
          <p className="text-xs text-gray-500">Taille</p>
          <p className="font-medium">{formatFileSize(doc.file_size)}</p>
        </div>
        {doc.extracted_data?.confidence_globale && (
          <div>
            <p className="text-xs text-gray-500">Confiance OCR</p>
            <p className="font-medium">
              {Math.round(doc.extracted_data.confidence_globale * 100)}%
            </p>
          </div>
        )}
        {doc.exercice && (
          <div>
            <p className="text-xs text-gray-500">Exercice</p>
            <p className="font-medium">{doc.exercice}</p>
          </div>
        )}
      </div>

      <Separator />

      {/* Summary for imported docs */}
      {doc.profile_data && (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-900">Resume</h4>
          <div className="grid grid-cols-2 gap-3 p-4 bg-gray-50 rounded-lg">
            {doc.profile_data.raison_sociale && (
              <div>
                <p className="text-xs text-gray-500">Raison sociale</p>
                <p className="font-medium">{doc.profile_data.raison_sociale}</p>
              </div>
            )}
            {doc.profile_data.siren && (
              <div>
                <p className="text-xs text-gray-500">SIREN</p>
                <p className="font-medium text-indigo-600">{doc.profile_data.siren}</p>
              </div>
            )}
            {doc.profile_data.capital_social && (
              <div>
                <p className="text-xs text-gray-500">Capital</p>
                <p className="font-medium">{doc.profile_data.capital_social.toLocaleString()} EUR</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Generated doc info */}
      {doc.source === "generated" && doc.metadata && (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-900">Details de generation</h4>
          <div className="grid grid-cols-2 gap-3 p-4 bg-cyan-50 rounded-lg">
            {doc.metadata.exercice && (
              <div>
                <p className="text-xs text-gray-500">Exercice</p>
                <p className="font-medium">{doc.metadata.exercice}</p>
              </div>
            )}
            {doc.metadata.generated_by && (
              <div>
                <p className="text-xs text-gray-500">Genere par</p>
                <p className="font-medium">{doc.metadata.generated_by}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action for unvalidated docs */}
      {doc.source === "imported" && !doc.applied && doc.status === "completed" && (
        <div className="pt-4 border-t">
          <Button
            className="w-full gap-2"
            onClick={() => {
              onNavigate && onNavigate("import");
              onClose();
            }}
          >
            <CheckCircle2 className="w-4 h-4" />
            Aller valider ce document
          </Button>
        </div>
      )}
    </div>
  );
}

// PDF Tab Component
function PdfTab({ pdfContent, pdfLoading, onReload }) {
  const [loadError, setLoadError] = React.useState(false);
  
  // Reset error state when content changes
  React.useEffect(() => {
    setLoadError(false);
  }, [pdfContent]);

  if (pdfLoading) {
    return (
      <div className="h-[500px] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        <span className="ml-2 text-gray-500">Chargement du PDF...</span>
      </div>
    );
  }
  
  if (pdfContent && !loadError) {
    return (
      <div className="h-[500px] flex flex-col">
        <div className="flex-1 bg-gray-50 rounded overflow-hidden border">
          <embed
            src={pdfContent}
            type="application/pdf"
            className="w-full h-full"
            style={{ minHeight: '420px' }}
            onError={() => setLoadError(true)}
          />
        </div>
        <div className="mt-3 flex justify-center gap-3">
          <a 
            href={pdfContent} 
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            <Eye className="w-4 h-4" />
            Ouvrir dans un nouvel onglet
          </a>
          <a 
            href={pdfContent} 
            download="document.pdf"
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
          >
            <Download className="w-4 h-4" />
            Telecharger
          </a>
        </div>
      </div>
    );
  }
  
  // Fallback when PDF content exists but embed failed
  if (pdfContent && loadError) {
    return (
      <div className="h-[500px] flex flex-col items-center justify-center">
        <FileText className="w-16 h-16 text-gray-300 mb-4" />
        <p className="text-gray-500 mb-4">L'apercu PDF n'est pas disponible dans ce navigateur</p>
        <div className="flex gap-3">
          <a 
            href={pdfContent} 
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            <Eye className="w-4 h-4" />
            Ouvrir dans un nouvel onglet
          </a>
          <a 
            href={pdfContent} 
            download="document.pdf"
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
          >
            <Download className="w-4 h-4" />
            Telecharger
          </a>
        </div>
      </div>
    );
  }
  
  return (
    <div className="h-[500px] flex items-center justify-center">
      <div className="text-center">
        <FileText className="w-12 h-12 mx-auto text-gray-300 mb-2" />
        <p className="text-gray-500">PDF non disponible</p>
        <Button
          variant="outline"
          size="sm"
          className="mt-2"
          onClick={onReload}
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          Recharger
        </Button>
      </div>
    </div>
  );
}

// Data Tab Component
function DataTab({ doc }) {
  if (!doc.extracted_data) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Database className="w-12 h-12 mx-auto text-gray-300 mb-2" />
          <p className="text-gray-500">Aucune donnee extraite</p>
        </div>
      </div>
    );
  }
  
  return (
    <ScrollArea className="h-full">
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900">Donnees extraites par l'OCR</h4>
          <Badge variant="outline">
            {Math.round((doc.extracted_data.confidence_globale || 0) * 100)}% confiance
          </Badge>
        </div>
        
        <div className="bg-gray-900 rounded-lg p-4 overflow-auto">
          <pre className="text-xs text-green-400 whitespace-pre-wrap font-mono">
            {JSON.stringify(doc.extracted_data.donnees || doc.extracted_data, null, 2)}
          </pre>
        </div>
        
        {doc.extracted_data.alertes?.length > 0 && (
          <div className="mt-4">
            <h5 className="font-medium text-amber-600 mb-2">Alertes</h5>
            <ul className="space-y-1">
              {doc.extracted_data.alertes.map((alert, idx) => (
                <li key={idx} className="text-sm text-amber-700 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {alert}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

// Knowledge Base Tab Component
function KnowledgeBaseTab({ knowledgeBase }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Company Info */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-5 h-5 text-indigo-500" />
            Informations Societe
          </CardTitle>
        </CardHeader>
        <CardContent>
          {knowledgeBase?.company ? (
            <div className="space-y-3">
              <InfoRow label="Raison sociale" value={knowledgeBase.company.raison_sociale} />
              <InfoRow label="Forme juridique" value={knowledgeBase.company.forme_juridique} />
              <InfoRow label="SIREN" value={knowledgeBase.company.siren} highlight />
              <InfoRow label="Capital" value={knowledgeBase.company.capital_social ? `${knowledgeBase.company.capital_social.toLocaleString()} EUR` : null} />
              <InfoRow label="Siege" value={knowledgeBase.company.siege_social?.ville} />
              <InfoRow label="Code APE" value={knowledgeBase.company.code_ape} />
            </div>
          ) : (
            <EmptyState icon={Building2} text="Importez des Statuts ou un Kbis" />
          )}
        </CardContent>
      </Card>

      {/* Associes */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-500" />
            Associes / Actionnaires
          </CardTitle>
        </CardHeader>
        <CardContent>
          {knowledgeBase?.associes?.length > 0 ? (
            <div className="space-y-2">
              {knowledgeBase.associes.map((a, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm">
                    {a.denomination || `${a.civilite || ''} ${a.prenom || ''} ${a.nom || ''}`}
                  </span>
                  <div className="text-right">
                    <span className="text-sm font-medium">{a.nombre_parts} parts</span>
                    <span className="text-xs text-indigo-600 ml-2">({a.pourcentage}%)</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={Users} text="Importez des Statuts" />
          )}
        </CardContent>
      </Card>

      {/* Financial Data */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Euro className="w-5 h-5 text-green-500" />
            Donnees Financieres
          </CardTitle>
        </CardHeader>
        <CardContent>
          {knowledgeBase?.exercices?.length > 0 ? (
            <div className="space-y-3">
              {knowledgeBase.exercices.slice(0, 3).map((ex, idx) => (
                <div key={idx} className="p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-green-800">Exercice {ex.annee}</span>
                    <Badge variant="outline" className="text-xs">{ex.source}</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-gray-500">CA</p>
                      <p className="font-medium">{ex.chiffre_affaires?.toLocaleString() || '-'} EUR</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Resultat net</p>
                      <p className={`font-medium ${ex.resultat_net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {ex.resultat_net?.toLocaleString() || '-'} EUR
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={Euro} text="Importez un Bilan" />
          )}
        </CardContent>
      </Card>

      {/* Useful Info */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            Informations Utiles
          </CardTitle>
        </CardHeader>
        <CardContent>
          {knowledgeBase?.useful_info?.length > 0 ? (
            <ScrollArea className="h-48">
              <div className="space-y-2">
                {knowledgeBase.useful_info.map((info, idx) => (
                  <div key={idx} className="p-2 bg-amber-50 rounded text-sm">
                    <p className="font-medium text-amber-800">{info.label}</p>
                    <p className="text-amber-700">{info.value}</p>
                    <p className="text-xs text-amber-500 mt-1">Source: {info.source}</p>
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <EmptyState icon={Lightbulb} text="Les infos supplementaires apparaitront ici" />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Timeline Tab Component
function TimelineTab({ timeline }) {
  if (timeline.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-lg border">
        <History className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun evenement</h3>
        <p className="text-gray-500">L'historique se construira au fur et a mesure des imports</p>
      </div>
    );
  }
  
  return (
    <div className="relative">
      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
      
      <div className="space-y-6">
        {timeline.map((event, idx) => {
          const config = CATEGORIES[event.type] || CATEGORIES.statuts;
          const Icon = config.icon;
          
          return (
            <div key={idx} className="relative flex gap-4 pl-10">
              <div className={`absolute left-2 p-1.5 rounded-full ${config.color} border-2 border-white shadow`}>
                <Icon className="w-3 h-3" />
              </div>
              
              <Card className="flex-1">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{event.title}</p>
                      <p className="text-sm text-gray-500 mt-1">{event.description}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-400">{formatDate(event.date)}</p>
                      <Badge variant="outline" className="mt-1 text-xs">{config.label}</Badge>
                    </div>
                  </div>
                  
                  {event.details && Object.keys(event.details).length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {Object.entries(event.details).slice(0, 4).map(([key, val]) => (
                          <div key={key}>
                            <p className="text-gray-500 text-xs">{key}</p>
                            <p className="font-medium">{String(val)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Helper Components
function InfoRow({ label, value, highlight = false }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500 text-sm">{label}</span>
      <span className={`text-sm font-medium ${value ? (highlight ? 'text-indigo-600' : 'text-gray-900') : 'text-gray-300'}`}>
        {value || "—"}
      </span>
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="text-center py-8 text-gray-500">
      <Icon className="w-10 h-10 mx-auto text-gray-300 mb-2" />
      <p className="text-sm">{text}</p>
    </div>
  );
}
